import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';

import { useAuth } from '@/context/auth.context';
import { useProfileSettings } from '@/context/profileSettings.context';
import type { Filters } from '@/context/useFilters/types';
import { toLatestYearDateRange } from '@/utils/format';

import { stringifyFiltersPayload, toFiltersPatch, toFiltersPayload, type FiltersPayload } from './lib/apiPayload';
import { NEW_FILTERS_OPTIONS } from './lib/const';
import { DEFAULT_FILTERS_DRAFT, compactFiltersDraft, getCustomFilterApiKeys, isDateRange } from './lib/parsers';
import { DEFAULT_PERIOD_COMPARISON, getPeriodComparisonState, sanitizePeriodComparison } from './lib/periodComparison';
import { enableSelectedCountries, useFilterDataLoader } from './lib/useFilterDataLoader';
import { loadNewFiltersFromStorage, newFiltersReducer, saveNewFiltersToStorage } from './lib/utils';

import type {
  FilterValueSets,
  NewFilters,
  NewFiltersActions,
  NewFiltersContextValue,
  NewFiltersPersistedState,
  NewFiltersProviderProps,
  NewFiltersState,
  PeriodComparisonResults,
} from './lib/types';

function withoutPersistedPeriod(state: NewFiltersPersistedState): NewFiltersPersistedState {
  const next = { ...(state as NewFiltersPersistedState & Partial<NewFilters>) };
  delete next.period;

  if (next.applied) {
    next.applied = { ...next.applied };
    delete next.applied.period;
  }

  if (next.draft) {
    next.draft = { ...next.draft };
    delete next.draft.period;
  }

  return next;
}

/** React context for the new filters model. Use `useNewFilters` in components. */
export const NewFiltersContext = createContext<NewFiltersContextValue | null>(null);

/**
 * Provides draft/applied filter state, available options, actions, and
 * localStorage persistence for the new filters bar.
 */
export function NewFiltersProvider({ children }: NewFiltersProviderProps) {
  const { auth } = useAuth();
  const { lang, dateRangeMinMax } = useProfileSettings();

  const groupId = auth?.selectedGroup?.id;
  const groupName = auth?.selectedGroup?.name;
  const [storedFilters] = useState<NewFiltersPersistedState | null>(() => loadNewFiltersFromStorage());
  const [periodComparisonSettings, setPeriodComparisonSettings] = useState(() =>
    sanitizePeriodComparison(storedFilters?.periodComparison)
  );
  const [periodComparisonResults, setPeriodComparisonResults] = useState<PeriodComparisonResults>({});
  const latestYearPeriod = useMemo(() => toLatestYearDateRange(dateRangeMinMax), [dateRangeMinMax]);
  const defaultPeriod = isDateRange(latestYearPeriod) ? latestYearPeriod : DEFAULT_FILTERS_DRAFT.period;
  const defaultFiltersWithPeriod = { ...DEFAULT_FILTERS_DRAFT, period: defaultPeriod };
  const defaultStateWithPeriod: NewFiltersState = {
    applied: defaultFiltersWithPeriod,
    draft: defaultFiltersWithPeriod,
  };

  const [state, dispatch] = useReducer(newFiltersReducer, defaultStateWithPeriod);
  const [hydrated, setHydrated] = useState(false);
  const lastAuthKeyRef = useRef<string | null>(null);

  const stateWithPeriod = useMemo<NewFiltersState>(() => {
    if (!isDateRange(latestYearPeriod)) return state;

    const applied = isDateRange(state.applied.period) ? state.applied : { ...state.applied, period: latestYearPeriod };
    const draft = isDateRange(state.draft.period) ? state.draft : { ...state.draft, period: latestYearPeriod };

    if (applied === state.applied && draft === state.draft) return state;

    return { applied, draft };
  }, [latestYearPeriod, state]);

  const draftFilterRequest = stateWithPeriod.draft;

  const authKey = useMemo(() => {
    if (!auth) return null;

    const userId = auth.user?.id ?? auth.user?.email ?? 'unknown-user';
    const selectedGroupId = auth.selectedGroup?.id ?? 'no-group';

    return `${userId}:${selectedGroupId}`;
  }, [auth]);

  const selectOnlyAvailableCountry = useCallback((countryId: string) => {
    dispatch({ filter: 'countries', type: 'setAppliedAndDraftMulti', value: [countryId] });
  }, []);

  const deselectUnavailableValues = useCallback((unavailable: FilterValueSets) => {
    dispatch({ type: 'deselectUnavailableValues', unavailable });
  }, []);

  const { exposedCustomFilterKeys, filterData, persistedCustomFilterKeys, visibleCustomFilters } = useFilterDataLoader({
    draftFilterRequest,
    groupId,
    groupName,
    hasSelectedCountry: state.applied.countries.length > 0 || state.draft.countries.length > 0,
    hydrated,
    lang,
    onSingleCountrySelected: selectOnlyAvailableCountry,
    onValuesUnavailable: deselectUnavailableValues,
  });

  useEffect(() => {
    if (!authKey || hydrated) return;

    const savedState = storedFilters ?? loadNewFiltersFromStorage();

    if (savedState) dispatch({ state: withoutPersistedPeriod(savedState), type: 'hydrate' });
    lastAuthKeyRef.current = authKey;
    setHydrated(true);
  }, [authKey, hydrated, storedFilters]);

  useEffect(() => {
    if (!hydrated || !authKey || lastAuthKeyRef.current !== authKey) return;

    saveNewFiltersToStorage(stateWithPeriod, persistedCustomFilterKeys, periodComparisonSettings);
  }, [authKey, hydrated, persistedCustomFilterKeys, periodComparisonSettings, stateWithPeriod]);

  useEffect(() => {
    if (!isDateRange(latestYearPeriod)) return;

    dispatch({
      filters: {
        period: latestYearPeriod,
      },
      type: 'setAppliedAndDraft',
    });
  }, [latestYearPeriod]);

  useEffect(() => {
    if (!hydrated) return;

    if (!authKey) {
      if (lastAuthKeyRef.current !== null) {
        lastAuthKeyRef.current = null;
        dispatch({ period: latestYearPeriod, type: 'resetToPeriod' });
      }

      return;
    }

    if (lastAuthKeyRef.current === null) {
      lastAuthKeyRef.current = authKey;
      return;
    }

    if (lastAuthKeyRef.current !== authKey) {
      lastAuthKeyRef.current = authKey;
      dispatch({ period: latestYearPeriod, type: 'resetToPeriod' });
    }
  }, [authKey, hydrated, latestYearPeriod]);

  const setDraftMulti = useCallback<NewFiltersActions['setDraftMulti']>((filter, value) => {
    dispatch({ filter, type: 'setDraftMulti', value });
  }, []);

  const setDraftHazardous = useCallback<NewFiltersActions['setDraftHazardous']>((value) => {
    dispatch({ type: 'setDraftHazardous', value });
  }, []);

  const clearDraftFilter = useCallback<NewFiltersActions['clearDraftFilter']>((filter) => {
    dispatch({ filter, type: 'clearDraftFilter' });
  }, []);

  const clearAppliedFilter = useCallback<NewFiltersActions['clearAppliedFilter']>((filter) => {
    dispatch({ filter, type: 'clearAppliedFilter' });
  }, []);

  const removeAppliedFilterValue = useCallback<NewFiltersActions['removeAppliedFilterValue']>((filter, value) => {
    dispatch({ filter, type: 'removeAppliedFilterValue', value });
  }, []);

  const clearEditableDraft = useCallback<NewFiltersActions['clearEditableDraft']>((options) => {
    dispatch({ options, type: 'clearEditableDraft' });
  }, []);

  const resetAllFilters = useCallback<NewFiltersActions['resetAllFilters']>((options) => {
    setPeriodComparisonSettings(DEFAULT_PERIOD_COMPARISON);
    setPeriodComparisonResults({});
    dispatch({ options, type: 'resetAllFilters' });
  }, []);

  const resetDraftToApplied = useCallback<NewFiltersActions['resetDraftToApplied']>(() => {
    dispatch({ type: 'resetDraftToApplied' });
  }, []);

  const setAppliedAndDraft = useCallback<NewFiltersActions['setAppliedAndDraft']>((filters) => {
    dispatch({ filters, type: 'setAppliedAndDraft' });
  }, []);

  const setPeriodComparison = useCallback<NewFiltersActions['setPeriodComparison']>(({ results, ...comparison }) => {
    setPeriodComparisonSettings((current) => ({
      ...current,
      ...comparison,
    }));

    if (results !== undefined || comparison.enabled === false || comparison.mode) {
      setPeriodComparisonResults(results ?? {});
    }
  }, []);

  const applyDraft = useCallback<NewFiltersActions['applyDraft']>(() => {
    dispatch({ type: 'applyDraft' });
  }, []);

  const actions = useMemo<NewFiltersActions>(
    () => ({
      applyDraft,
      clearAppliedFilter,
      clearDraftFilter,
      clearEditableDraft,
      removeAppliedFilterValue,
      resetAllFilters,
      resetDraftToApplied,
      setAppliedAndDraft,
      setDraftHazardous,
      setDraftMulti,
      setPeriodComparison,
    }),
    [
      applyDraft,
      clearAppliedFilter,
      clearDraftFilter,
      clearEditableDraft,
      removeAppliedFilterValue,
      resetAllFilters,
      resetDraftToApplied,
      setAppliedAndDraft,
      setDraftHazardous,
      setDraftMulti,
      setPeriodComparison,
    ]
  );

  const periodComparison = useMemo(
    () =>
      getPeriodComparisonState({
        dateRangeMinMax,
        primaryRange: stateWithPeriod.applied.period,
        results: periodComparisonResults,
        settings: periodComparisonSettings,
      }),
    [dateRangeMinMax, periodComparisonResults, periodComparisonSettings, stateWithPeriod.applied.period]
  );

  const defaultFilters = useMemo(
    () => enableSelectedCountries(filterData.default, state.draft.countries),
    [filterData.default, state.draft.countries]
  );

  const value = useMemo<NewFiltersContextValue>(
    () => ({
      applied: compactFiltersDraft(stateWithPeriod.applied, exposedCustomFilterKeys),
      actions,
      custom: visibleCustomFilters,
      default: defaultFilters,
      draft: compactFiltersDraft(stateWithPeriod.draft, exposedCustomFilterKeys),
      options: NEW_FILTERS_OPTIONS,
      periodComparison,
    }),
    [actions, defaultFilters, exposedCustomFilterKeys, periodComparison, stateWithPeriod, visibleCustomFilters]
  );

  return <NewFiltersContext.Provider value={value}>{children}</NewFiltersContext.Provider>;
}

/** Returns the new filters context and enforces provider usage. */
export function useNewFilters(): NewFiltersContextValue {
  const context = useContext(NewFiltersContext);

  if (!context) throw new Error('useNewFilters must be used within NewFiltersProvider');

  return context;
}

export function useFiltersPayload(): FiltersPayload {
  const { applied, custom } = useNewFilters();
  const allowedCustomFilterKeys = useMemo(() => getCustomFilterApiKeys(custom), [custom]);

  return useMemo(() => toFiltersPayload(applied, allowedCustomFilterKeys), [allowedCustomFilterKeys, applied]);
}

export function useFiltersJson(): string {
  const { applied, custom } = useNewFilters();
  const allowedCustomFilterKeys = useMemo(() => getCustomFilterApiKeys(custom), [custom]);

  return useMemo(() => stringifyFiltersPayload(applied, allowedCustomFilterKeys), [allowedCustomFilterKeys, applied]);
}

export function useApplyFiltersPatch(): (filters: Partial<Filters>) => void {
  const {
    actions: { setAppliedAndDraft },
  } = useNewFilters();

  return useCallback((filters) => setAppliedAndDraft(toFiltersPatch(filters)), [setAppliedAndDraft]);
}
